import { TilesError } from '@/plugins/tiles/TilesError'
import type { TilesRuntimeOptions } from '@/plugins/tiles/types'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('tiles:scheduler')
export type TileTaskKind = 'download' | 'decode'
export type TileSchedulerOptions = Pick<
  TilesRuntimeOptions,
  'maxConcurrentRequests' | 'maxConcurrentDecodes' | 'maxRetries' | 'retryDelayMs'
>
export interface TileTaskOptions<T> {
  readonly signal?: AbortSignal
  /** 优先级需求归属；默认使用 signal。只用于分组更新，不改变消费者取消语义。 */
  readonly prioritySignal?: AbortSignal
  /** 仅在所有消费者已撤销后到达的结果调用一次；已交付结果由消费者持有。 */
  readonly cleanup?: (value: T) => void
  readonly retryable?: (error: unknown) => boolean
}
export interface TileQueueStats {
  readonly queued: number
  readonly active: number
  readonly backoff: number
}
interface Consumer {
  readonly prioritySignal?: AbortSignal
  priority: number
  resolve(value: unknown): void
  reject(error: unknown): void
  detach(): void
}
interface Task {
  readonly id: string
  readonly kind: TileTaskKind
  readonly controller: AbortController
  readonly run: (signal: AbortSignal) => Promise<unknown>
  readonly cleanup?: (value: unknown) => void
  readonly retryable?: (error: unknown) => boolean
  readonly consumers: Set<Consumer>
  readonly enqueuedAt: number
  readonly sequence: number
  priority: number
  attempts: number
  state: 'queued' | 'active' | 'backoff' | 'cancelled'
  timer?: ReturnType<typeof setTimeout>
}

const cancelled = () =>
  new TilesError({ code: 'cancelled', stage: 'request', message: 'Tiles 任务需求已撤销' })

/** Record 内的双队列；任务自动推进，不通过渲染帧轮询异步等待。 */
export class TileScheduler {
  private _options: TileSchedulerOptions
  private _tasks = new Map<string, Task>()
  private _active = { download: 0, decode: 0 }
  private _closed = false
  private _pumpScheduled = false
  private _sequence = 0

  constructor(
    options: TileSchedulerOptions,
    private readonly _onChange?: () => void,
    private readonly _onCleanupError?: (error: unknown) => void
  ) {
    this._options = this._validate(options)
  }

  /** 更高 priority 优先；等待超过一秒的队列任务按先入顺序提升，避免持续新任务饥饿。 */
  schedule<T>(
    key: string,
    kind: TileTaskKind,
    priority: number,
    run: (signal: AbortSignal) => Promise<T>,
    options: TileTaskOptions<T> = {}
  ): Promise<T> {
    if (this._closed || options.signal?.aborted) return Promise.reject(cancelled())
    const id = `${kind}:${key}`
    let task = this._tasks.get(id)
    if (!task) {
      task = {
        id,
        kind,
        controller: new AbortController(),
        run,
        cleanup: options.cleanup as ((value: unknown) => void) | undefined,
        retryable: options.retryable,
        consumers: new Set(),
        enqueuedAt: Date.now(),
        sequence: this._sequence++,
        priority: Number.isFinite(priority) ? priority : 0,
        attempts: 0,
        state: 'queued',
      }
      this._tasks.set(id, task)
    }
    const current = task
    const promise = new Promise<T>((resolve, reject) => {
      const consumer: Consumer = {
        prioritySignal: options.prioritySignal ?? options.signal,
        priority: Number.isFinite(priority) ? priority : 0,
        resolve: (value) => resolve(value as T),
        reject,
        detach: () => options.signal?.removeEventListener('abort', abort),
      }
      const abort = () => {
        if (!current.consumers.delete(consumer)) return
        consumer.detach()
        reject(cancelled())
        if (!current.consumers.size) this._cancel(current)
        else {
          this._refreshPriority(current)
          this._requestPump()
        }
      }
      current.consumers.add(consumer)
      this._refreshPriority(current)
      options.signal?.addEventListener('abort', abort, { once: true })
    })
    this._changed()
    this._requestPump()
    return promise
  }

  /**
   * 用最新视图优先级更新此需求的消费者，可升也可降；共享任务取有效消费者的最大值。
   * 返回发生更新的任务数。活动任务不抢占，退避保留次数/时限，等待年龄与 FIFO 顺序不变。
   */
  reprioritize(prioritySignal: AbortSignal, priority: number): number {
    return this.reprioritizeMany(new Map([[prioritySignal, priority]]))
  }

  /** 一次遍历更新整批需求；未命中消费者保持原值，任务寿命与队列政策不变。 */
  reprioritizeMany(priorities: ReadonlyMap<AbortSignal, number>): number {
    if (this._closed || !priorities.size) return 0
    let updated = 0
    for (const task of this._tasks.values()) {
      let changed = false
      let priority = -Infinity
      for (const consumer of task.consumers) {
        const signal = consumer.prioritySignal
        if (signal && !signal.aborted && priorities.has(signal)) {
          const value = priorities.get(signal)!
          const next = Number.isFinite(value) ? value : 0
          if (consumer.priority !== next) {
            consumer.priority = next
            changed = true
          }
        }
        priority = Math.max(priority, consumer.priority)
      }
      if (changed) {
        task.priority = priority
        updated++
      }
    }
    if (updated) this._requestPump()
    return updated
  }

  configure(options: Partial<TileSchedulerOptions>): void {
    this._options = this._validate({ ...this._options, ...options })
    this._requestPump()
  }

  /** 队列通过微任务与定时器推进；这里不要求额外视觉帧。 */
  get hasImmediateWork(): boolean {
    return false
  }

  getStats(): { downloads: TileQueueStats; decodes: TileQueueStats } {
    const count = (kind: TileTaskKind): TileQueueStats => {
      const tasks = [...this._tasks.values()].filter((task) => task.kind === kind)
      return {
        queued: tasks.filter((task) => task.state === 'queued').length,
        active: this._active[kind],
        backoff: tasks.filter((task) => task.state === 'backoff').length,
      }
    }
    return { downloads: count('download'), decodes: count('decode') }
  }

  dispose(): void {
    if (this._closed) return
    this._closed = true
    for (const task of [...this._tasks.values()]) this._cancel(task)
  }

  private _validate(options: TileSchedulerOptions): TileSchedulerOptions {
    if (
      !Number.isInteger(options.maxConcurrentRequests) ||
      options.maxConcurrentRequests < 1 ||
      !Number.isInteger(options.maxConcurrentDecodes) ||
      options.maxConcurrentDecodes < 1 ||
      !Number.isInteger(options.maxRetries) ||
      options.maxRetries < 0 ||
      !Number.isFinite(options.retryDelayMs) ||
      options.retryDelayMs < 0
    )
      throw new TilesError({
        code: 'invalid-input',
        stage: 'runtime',
        message: 'Tiles 调度配置无效',
      })
    return { ...options }
  }

  private _changed(): void {
    try {
      this._onChange?.()
    } catch (error) {
      log.error(`状态回调失败: ${String(error)}`)
    }
  }

  private _refreshPriority(task: Task): void {
    let priority = -Infinity
    for (const consumer of task.consumers) priority = Math.max(priority, consumer.priority)
    task.priority = priority
  }

  private _requestPump(): void {
    if (this._closed || this._pumpScheduled) return
    this._pumpScheduled = true
    queueMicrotask(() => {
      this._pumpScheduled = false
      if (this._closed) return
      for (const kind of ['download', 'decode'] as const) {
        const limit =
          kind === 'download'
            ? this._options.maxConcurrentRequests
            : this._options.maxConcurrentDecodes
        while (this._active[kind] < limit) {
          const queued = [...this._tasks.values()].filter(
            (task) => task.kind === kind && task.state === 'queued'
          )
          const now = Date.now()
          queued.sort((a, b) => {
            const agedA = now - a.enqueuedAt >= 1000
            const agedB = now - b.enqueuedAt >= 1000
            if (agedA !== agedB) return agedA ? -1 : 1
            return agedA
              ? a.sequence - b.sequence
              : b.priority - a.priority || a.sequence - b.sequence
          })
          const task = queued[0]
          if (!task) break
          this._start(task)
        }
      }
    })
  }

  private _start(task: Task): void {
    task.state = 'active'
    task.attempts++
    this._active[task.kind]++
    this._changed()
    Promise.resolve()
      .then(() => {
        if (task.controller.signal.aborted) throw cancelled()
        return task.run(task.controller.signal)
      })
      .then(
        (value) => {
          if (task.state === 'cancelled' || this._closed || !task.consumers.size) {
            try {
              task.cleanup?.(value)
            } catch (error) {
              try {
                this._onCleanupError?.(error)
              } catch {
                /* 清理诊断不得破坏队列退出。 */
              }
              log.error(`迟到资源清理失败: ${String(error)}`)
            }
          } else {
            this._remove(task)
            for (const consumer of task.consumers) {
              consumer.detach()
              consumer.resolve(value)
            }
            task.consumers.clear()
          }
        },
        (error: unknown) => {
          if (task.state === 'cancelled' || this._closed) return
          let retryable = error instanceof TilesError && error.diagnostic.retryable === true
          try {
            retryable = task.retryable?.(error) ?? retryable
          } catch {
            retryable = false
          }
          if (
            (error instanceof Error && error.name === 'AbortError') ||
            (error instanceof TilesError &&
              [
                'cancelled',
                'disposed',
                'unsupported-version',
                'unsupported-capability',
                'unsupported-content',
                'invalid-input',
                'invalid-resource',
              ].includes(error.diagnostic.code))
          )
            retryable = false
          if (
            retryable &&
            task.attempts <= this._options.maxRetries &&
            task.consumers.size &&
            !task.controller.signal.aborted
          ) {
            task.state = 'backoff'
            task.timer = setTimeout(
              () => {
                task.timer = undefined
                if (this._closed || task.state === 'cancelled' || !task.consumers.size) return
                task.state = 'queued'
                this._changed()
                this._requestPump()
              },
              Math.min(30000, this._options.retryDelayMs * 2 ** (task.attempts - 1))
            )
          } else {
            this._remove(task)
            // run 可能已并发启动外部依赖；最终失败必须撤回这一消费者的全部需求。
            task.controller.abort()
            for (const consumer of task.consumers) {
              consumer.detach()
              consumer.reject(error)
            }
            task.consumers.clear()
          }
        }
      )
      .finally(() => {
        this._active[task.kind]--
        this._changed()
        this._requestPump()
      })
  }

  private _remove(task: Task): void {
    if (this._tasks.get(task.id) === task) this._tasks.delete(task.id)
  }

  private _cancel(task: Task): void {
    task.state = 'cancelled'
    task.controller.abort()
    clearTimeout(task.timer)
    this._remove(task)
    for (const consumer of task.consumers) {
      consumer.detach()
      consumer.reject(cancelled())
    }
    task.consumers.clear()
    this._changed()
    this._requestPump()
  }
}
